// charity-elegant — Charity gala / fundraiser preview card (c07 Xayriya).
//
// An elegant, mission-driven preview tile for the "MEHR-O-MUHABBAT GALA"
// black-tie benefit. Warm cream background, antique-gold border, a small
// heart-with-hands SVG icon at the top, a sepia-tinted past-gala photo
// in the middle, and the gala name set in Italiana display italic.
// Beneath, the mission line — "BENEFITING ORPHAN EDUCATION FUND" — and
// the date sit in tracked-out caps.
//
// All sizing is %/em-based so the card looks correct in the catalog grid,
// the editor preview, and printed at A6.

import { Photo, type LayoutCtx } from "./_shared";

// ─── Heart cradled in two hands ──────────────────────────────────────────
// A small, refined SVG: two open palms reaching upward with a heart cradled
// between them. Drawn in antique gold. Used both in the layout card and as
// the eyebrow ornament in the variant hero.
function HeartHands({ accent, ink }: { accent: string; ink: string }) {
  return (
    <svg
      viewBox="0 0 64 56"
      width="100%"
      height="100%"
      aria-hidden
      style={{ display: "block" }}
    >
      {/* Left palm */}
      <path
        d="M 6 36 Q 4 28 8 22 Q 12 18 18 22 Q 22 26 22 32 Q 22 38 26 42 Q 22 46 16 46 Q 8 46 6 36 Z"
        fill={accent}
        opacity="0.85"
      />
      {/* Right palm */}
      <path
        d="M 58 36 Q 60 28 56 22 Q 52 18 46 22 Q 42 26 42 32 Q 42 38 38 42 Q 42 46 48 46 Q 56 46 58 36 Z"
        fill={accent}
        opacity="0.85"
      />
      {/* Heart cradled between */}
      <path
        d="M 32 14 C 28 6 18 8 18 18 C 18 26 28 32 32 36 C 36 32 46 26 46 18 C 46 8 36 6 32 14 Z"
        fill={accent}
      />
      {/* Heart highlight */}
      <path
        d="M 24 14 Q 22 18 24 22"
        stroke="#FFFFFF"
        strokeWidth="1.2"
        fill="none"
        opacity="0.55"
        strokeLinecap="round"
      />
      {/* Subtle rim on heart */}
      <path
        d="M 32 14 C 28 6 18 8 18 18 C 18 26 28 32 32 36 C 36 32 46 26 46 18 C 46 8 36 6 32 14 Z"
        stroke={ink}
        strokeWidth="0.4"
        fill="none"
        opacity="0.35"
      />
    </svg>
  );
}

export function CharityElegantLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
  photos,
}: LayoutCtx) {
  const heroPhoto = photo || photos[0];

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: bg,
        color: ink,
        padding: "6%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Outer gold-bordered card — double rule for an old-world benefit feel */}
      <div
        style={{
          position: "relative",
          flex: 1,
          border: `1px solid ${accent}`,
          padding: "5% 6% 6%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 4px ${bg}, inset 0 0 0 5px ${accent}55`,
        }}
      >
        {/* Heart-with-hands icon at the top */}
        <div style={{ width: "14%", aspectRatio: "64 / 56", marginBottom: "3%" }}>
          <HeartHands accent={accent} ink={ink} />
        </div>

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "2.2%",
            fontWeight: 700,
            letterSpacing: "0.36em",
            color: accent,
            marginBottom: "4%",
          }}
        >
          CHARITY GALA · 2026
        </div>

        {/* Center: past-gala photo */}
        <div
          style={{
            width: "70%",
            aspectRatio: "16 / 9",
            marginBottom: "4%",
            position: "relative",
            overflow: "hidden",
            border: `1px solid ${accent}66`,
          }}
        >
          <Photo
            src={heroPhoto}
            sepia
            style={{
              position: "absolute",
              inset: 0,
              filter: "sepia(0.35) saturate(0.85) brightness(0.95)",
            }}
            fallback={
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${accent}33, ${ink}22)`,
                }}
              />
            }
          />
          {/* warm tint overlay to lock the cream + gold palette */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(135deg, ${accent}18, ${bg}22)`,
              pointerEvents: "none",
            }}
          />
        </div>

        {/* Gala name — Italiana italic display */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: "5.4%",
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
            letterSpacing: "0.005em",
            marginBottom: "3%",
            maxWidth: "92%",
          }}
        >
          {names}
        </div>

        {/* Hairline divider with center diamond */}
        <div
          aria-hidden
          style={{
            width: "44%",
            display: "flex",
            alignItems: "center",
            gap: "6%",
            marginBottom: "3%",
          }}
        >
          <span style={{ flex: 1, height: 1, background: accent, opacity: 0.55 }} />
          <span style={{ color: accent, fontSize: "2%" }}>◆</span>
          <span style={{ flex: 1, height: 1, background: accent, opacity: 0.55 }} />
        </div>

        {/* Mission line */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "1.9%",
            fontWeight: 600,
            letterSpacing: "0.34em",
            color: accent,
            marginBottom: "2%",
          }}
        >
          BENEFITING ORPHAN EDUCATION FUND
        </div>

        {/* Date · Venue */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "1.7%",
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: ink,
            opacity: 0.85,
          }}
        >
          {date} · {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
